import { useState, useEffect, useCallback } from 'react';
import type { TaskResource, TaskResourceEmail, TaskResourceText } from '@/types/task';
import { EditableEmailResource, EditableTextResource } from './EditableResource';
import { useUpdateTaskResources } from '@/hooks/useTasks';
import CopyButton from '@/components/common/CopyButton';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import Spinner from '@/components/common/Spinner';

interface TaskResourcesProps {
  resources: TaskResource[];
  taskId?: string;
  editable?: boolean;
}

/** Regex for matching email addresses within a string (non-anchored, global) */
const EMAIL_REGEX_GLOBAL = /[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+/g;

/** Helper to extract all email addresses from a string */
function extractEmails(value: string): string[] {
  return Array.from(value.matchAll(EMAIL_REGEX_GLOBAL), (m) => m[0]);
}

export default function TaskResources({ resources, taskId, editable = false }: TaskResourcesProps) {
  // Group resources by type, preserving original indices
  const links = resources
    .map((r, i) => ({ resource: r, index: i }))
    .filter((item) => item.resource.type === 'link');

  const phones = resources
    .map((r, i) => ({ resource: r, index: i }))
    .filter((item) => item.resource.type === 'phone');

  const contentDrafts = resources
    .map((r, i) => ({ resource: r, index: i }))
    .filter((item) => {
      if (item.resource.type === 'text') return true;
      if (item.resource.type === 'email') {
        return extractEmails((item.resource as TaskResourceEmail).address).length > 0;
      }
      return false;
    });

  // Extract unique valid email addresses from email resources
  // Each address field may contain multiple email addresses (e.g. comma-separated)
  const emailAddresses = Array.from(
    new Set(
      resources
        .filter((r): r is TaskResourceEmail => r.type === 'email')
        .flatMap((r) => extractEmails(r.address))
    )
  );

  return (
    <div className="space-y-6">
      {/* Links section */}
      {links.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-neutral-700">Links</h3>
          <div className="space-y-3">
            {links.map((item) => (
              <LinkItem key={item.index} resource={item.resource as TaskResource & { type: 'link' }} />
            ))}
          </div>
        </div>
      )}

      {/* Email Addresses section */}
      {emailAddresses.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-neutral-700">Email Addresses</h3>
          <div className="space-y-3">
            {emailAddresses.map((address) => (
              <EmailAddressItem key={address} address={address} />
            ))}
          </div>
        </div>
      )}

      {/* Phone Numbers section */}
      {phones.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-neutral-700">Phone Numbers</h3>
          <div className="space-y-3">
            {phones.map((item) => (
              <PhoneItem key={item.index} resource={item.resource as TaskResource & { type: 'phone' }} />
            ))}
          </div>
        </div>
      )}

      {/* Content Drafts section */}
      {contentDrafts.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold text-neutral-700">Content Drafts</h3>
          <p className="text-sm text-neutral-600">The drafts below are provided as a starting point. Edit them to make them your own.</p>
          <div className="space-y-3">
            {contentDrafts.map((item) => (
              <ContentDraftItem
                key={`${taskId}-${item.index}`}
                resource={item.resource as TaskResourceEmail | TaskResourceText}
                resourceIndex={item.index}
                taskId={taskId}
                allResources={resources}
                editable={editable}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Link resource item - unchanged from original */
function LinkItem({ resource }: { resource: TaskResource & { type: 'link' } }) {
  return (
    <Card>
      <div className="flex items-center gap-2">
        <svg className="h-5 w-5 flex-shrink-0 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
        >
          {resource.label}
        </a>
      </div>
    </Card>
  );
}

/** Email address item - displayed as a mailto link */
function EmailAddressItem({ address }: { address: string }) {
  return (
    <Card>
      <div className="flex items-center gap-2">
        <svg className="h-5 w-5 flex-shrink-0 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <a
          href={`mailto:${address}`}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
        >
          {address}
        </a>
      </div>
    </Card>
  );
}

/** Phone resource item - unchanged from original */
function PhoneItem({ resource }: { resource: TaskResource & { type: 'phone' } }) {
  return (
    <Card>
      <div className="flex items-center gap-2">
        <svg className="h-5 w-5 flex-shrink-0 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
        <div>
          <span className="text-sm font-medium text-neutral-900">{resource.label}</span>
          <span className="ml-2 text-sm text-neutral-600">{resource.number}</span>
        </div>
        <CopyButton text={resource.number} label="Copy" className="ml-auto" />
      </div>
    </Card>
  );
}

/**
 * Content draft item with reveal-behind-button behavior.
 *
 * Shows a label and a "Generate" button. When clicked, displays a spinner
 * for 2 seconds then reveals the editable draft content. The revealed state
 * is persisted to the backend via the resource's `revealed` field.
 *
 * For inactive tasks, the button is disabled (content can still be seen if
 * previously revealed).
 */
function ContentDraftItem({
  resource,
  resourceIndex,
  taskId,
  allResources,
  editable,
}: {
  resource: TaskResourceEmail | TaskResourceText;
  resourceIndex: number;
  taskId?: string;
  allResources: TaskResource[];
  editable: boolean;
}) {
  const isEmail = resource.type === 'email';
  const isRevealed = resource.revealed === true;

  // Track the generation lifecycle: 'idle' | 'generating' | 'done'
  const [generationState, setGenerationState] = useState<'idle' | 'generating' | 'done'>(
    isRevealed ? 'done' : 'idle'
  );

  const updateResources = useUpdateTaskResources();

  // Sync local state if the resource's revealed field changes (e.g., after refetch)
  // Only promote to 'done' if we're not currently in the middle of generating
  useEffect(() => {
    if (resource.revealed && generationState === 'idle') {
      setGenerationState('done');
    }
  }, [resource.revealed]);

  const handleGenerate = useCallback(() => {
    setGenerationState('generating');

    // Persist revealed status to backend
    if (taskId) {
      const updatedResources = allResources.map((r, i) => {
        if (i === resourceIndex) {
          return { ...r, revealed: true } as TaskResource;
        }
        return r;
      });

      updateResources.mutate({ taskId, data: { resources: updatedResources } });
    }

    // Show spinner for 2 seconds, then reveal content
    setTimeout(() => {
      setGenerationState('done');
    }, 2000);
  }, [taskId, resourceIndex, allResources, updateResources]);

  const label = isEmail
    ? ((resource as TaskResourceEmail).label?.trim()
      || `Email Draft - ${(resource as TaskResourceEmail).address}`)
    : (resource as TaskResourceText).label;

  const buttonText = isEmail ? 'Show Email Draft' : 'Show Draft';

  return (
    <Card>
      <div className="space-y-2">
        {/* Label span */}
        <div className="flex items-center gap-2">
          <svg className="h-5 w-5 flex-shrink-0 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {isEmail ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            )}
          </svg>
          <span className="text-sm font-medium text-neutral-900">{label}</span>
        </div>

        {/* Button / Spinner / Content - mutually exclusive states */}
        {generationState === 'idle' && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleGenerate}
            disabled={!editable}
          >
            {buttonText}
          </Button>
        )}

        {generationState === 'generating' && (
          <Spinner size="sm" className="justify-start py-2" />
        )}

        {generationState === 'done' && (
          <RevealedDraftContent
            resource={resource}
            resourceIndex={resourceIndex}
            taskId={taskId}
            allResources={allResources}
            editable={editable}
          />
        )}
      </div>
    </Card>
  );
}

/**
 * Renders the revealed draft content (email or text) using the existing
 * editable/read-only resource components.
 */
function RevealedDraftContent({
  resource,
  resourceIndex,
  taskId,
  allResources,
  editable,
}: {
  resource: TaskResourceEmail | TaskResourceText;
  resourceIndex: number;
  taskId?: string;
  allResources: TaskResource[];
  editable: boolean;
}) {
  if (resource.type === 'email') {
    if (editable && taskId) {
      return (
        <EditableEmailResource
          resource={resource}
          resourceIndex={resourceIndex}
          taskId={taskId}
          allResources={allResources}
          embedded
        />
      );
    }
    return (
      <ReadOnlyEmailContent resource={resource} />
    );
  }

  // Text resource
  if (editable && taskId) {
    return (
      <EditableTextResource
        resource={resource}
        resourceIndex={resourceIndex}
        taskId={taskId}
        allResources={allResources}
        embedded
      />
    );
  }
  return (
    <ReadOnlyTextContent resource={resource} />
  );
}

/** Read-only email draft content (for inactive tasks) */
function ReadOnlyEmailContent({ resource }: { resource: TaskResourceEmail }) {
  return (
    <div className="space-y-2">
      <div className="rounded-lg bg-primary-50 p-3 text-sm">
        <div className="mb-1">
          <span className="font-medium text-neutral-500">To: </span>
          <span className="text-neutral-700">{resource.address}</span>
          <CopyButton text={resource.address} label="Copy" className="ml-2" />
        </div>
        <div className="mb-2">
          <span className="font-medium text-neutral-500">Subject: </span>
          <span className="text-neutral-700">{resource.subject}</span>
          <CopyButton text={resource.subject} label="Copy" className="ml-2" />
        </div>
        <div className="border-t border-neutral-200 pt-2">
          <pre className="whitespace-pre-wrap text-neutral-700">{resource.body}</pre>
        </div>
      </div>
      <CopyButton
        text={`To: ${resource.address}\nSubject: ${resource.subject}\n\n${resource.body}`}
        label="Copy All"
      />
    </div>
  );
}

/** Read-only text draft content (for inactive tasks) */
function ReadOnlyTextContent({ resource }: { resource: TaskResourceText }) {
  return (
    <div className="space-y-2">
      <div className="rounded-lg bg-primary-50 p-3">
        <pre className="whitespace-pre-wrap text-sm text-neutral-700">{resource.content}</pre>
      </div>
      <CopyButton text={resource.content} label="Copy" />
    </div>
  );
}
